/**
 * Authenticated transport for the Go gateway.
 *
 * Each request pins the account `AuthBridge` has bound when it starts
 * (`authSession.ts`): it is sent only with that account's token, and its
 * response is dropped if the signed-in account changed while it was in flight.
 * Until a session is bound, requests go out unauthenticated (and protected
 * routes answer 401).
 */

import { pinAuthAccount, type AuthPin } from './authSession';

/** The gateway origin every request targets (also shown in playground code snippets). */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8080';

/** Error carrying the gateway's HTTP status, so callers can branch on 401 etc. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
    public readonly retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** GETs a gateway path with the current Clerk token attached, parsing JSON. */
export const authedGet = async <T>(path: string): Promise<T> => {
  return (await authedExchange<T>('GET', path)).body;
};

/** POSTs a JSON body to a gateway path with the current Clerk token attached. */
export const authedPost = async <T>(path: string, body: unknown): Promise<T> => {
  return authedRequest<T>('POST', path, body);
};

/** Sends an authenticated JSON request using gateway-authored operation facts. */
export const authedRequest = async <T>(
  method: string,
  path: string,
  body: unknown,
  options: AuthedRequestOptions = {},
): Promise<T> => {
  return (await authedExchange<T>(method, path, body, options)).body;
};

export interface AuthedRequestOptions {
  readonly headers?: Readonly<Record<string, string>>;
  readonly signal?: AbortSignal;
}

export interface AuthedResponse<T> {
  readonly status: number;
  readonly headers: Headers;
  readonly body: T;
}

/** Sends an authenticated request while preserving response metadata needed by
 * protocols such as durable 202 continuation. */
export const authedExchange = async <T>(
  method: string,
  path: string,
  body?: unknown,
  options: AuthedRequestOptions = {},
): Promise<AuthedResponse<T>> => {
  const pin = pinAuthAccount();
  const token = await pin.token();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...(method === 'GET' ? {} : { method }),
    headers: {
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });
  return { status: res.status, headers: res.headers, body: await parsePinned<T>(pin, res) };
};

/** DELETEs a gateway path with the current Clerk token attached, parsing JSON. */
export const authedDelete = async <T>(path: string): Promise<T> => {
  const pin = pinAuthAccount();
  const token = await pin.token();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  return parsePinned<T>(pin, res);
};

/** Parses a response only for the account that sent it; a stale one is dropped. */
const parsePinned = async <T>(pin: AuthPin, res: Response): Promise<T> => {
  try {
    const body = await parse<T>(res);
    pin.assertCurrent();
    return body;
  } catch (error) {
    pin.assertCurrent();
    throw error;
  }
};

/** Parses a gateway response, throwing ApiError (with status) on non-2xx. */
const parse = async <T>(res: Response): Promise<T> => {
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      error?: string;
      code?: string;
      retryAfterSeconds?: number;
    } | null;
    throw new ApiError(
      res.status,
      body?.error ?? `request failed (${res.status})`,
      body?.code,
      body?.retryAfterSeconds,
    );
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
};
